import React, { useState } from "react";
import HeaderNav from "@/components/HeaderNav";
import Footer from "@/components/Footer";
import { api, formatApiError, formatINR } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, PackageSearch, CircleDashed, CircleDot, CheckCircle2, XCircle, IndianRupee, MapPin, Clock, Phone } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { key: "pending", label: "Booked", desc: "Pickup scheduled — our agent will visit soon", Icon: CircleDot },
  { key: "inspecting", label: "Inspecting", desc: "Agent is verifying your device on the spot", Icon: PackageSearch },
  { key: "completed", label: "Cash Paid", desc: "Payout completed via Cash / UPI", Icon: CheckCircle2 },
];

const SLOT_LABELS = {
  morning: "Morning (10 AM – 1 PM)",
  afternoon: "Afternoon (1 PM – 5 PM)",
  evening: "Evening (5 PM – 9 PM)",
};

export default function OrderTrack() {
  const [ref, setRef] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!ref || !phone) {
      toast.error("Enter both reference and phone");
      return;
    }
    setLoading(true);
    setOrder(null);
    try {
      const { data } = await api.get(`/track?ref=${encodeURIComponent(ref.trim().toUpperCase())}&phone=${encodeURIComponent(phone.trim())}`);
      setOrder(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="track-page">
      <HeaderNav />
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 tv-radial pointer-events-none" />
        <div className="absolute inset-0 tv-grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-center mb-8">
            <span className="tv-chip mb-4">
              <PackageSearch className="w-3.5 h-3.5" /> Order Tracking
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Track your pickup status
            </h1>
            <p className="text-muted-foreground mt-2">
              Enter your reference number (from the confirmation) and phone to see live status.
            </p>
          </div>

          <Card className="mb-8">
            <CardContent className="p-6">
              <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="track-form">
                <div className="grid gap-1.5">
                  <Label htmlFor="ref">Reference Number</Label>
                  <Input
                    id="ref"
                    value={ref}
                    onChange={(e) => setRef(e.target.value.toUpperCase())}
                    placeholder="TV-9001"
                    className="font-mono uppercase"
                    data-testid="track-ref-input"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ph">Phone (full or last 4 digits)</Label>
                  <Input
                    id="ph"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. 2222"
                    data-testid="track-phone-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-primary hover:bg-primary/90 h-10"
                  data-testid="track-submit-btn"
                >
                  <Search className="w-4 h-4 mr-1.5" />
                  {loading ? "Searching…" : "Track"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {order && <OrderStatusTimeline order={order} />}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function OrderStatusTimeline({ order }) {
  const cancelled = order.status === "cancelled";
  const activeIdx = STAGES.findIndex((s) => s.key === order.status);

  return (
    <div className="space-y-6" data-testid="track-result">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Reference</div>
              <CardTitle className="mt-1 font-mono">{order.ref}</CardTitle>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Payout</div>
              <div className="mt-1 flex items-end gap-0.5 justify-end text-accent">
                <IndianRupee className="w-5 h-5" />
                <span className="text-2xl font-extrabold font-mono" data-testid="track-payout">
                  {order.final_price.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid sm:grid-cols-3 gap-4 text-sm border-t border-border pt-4">
            <div className="flex gap-2">
              <PackageSearch className="w-4 h-4 mt-0.5 text-primary" />
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Device</div>
                <div className="font-medium">{order.product_name}</div>
                <div className="text-muted-foreground">{order.storage}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Clock className="w-4 h-4 mt-0.5 text-primary" />
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Pickup</div>
                <div className="font-medium">{order.pickup_date}</div>
                <div className="text-muted-foreground">{SLOT_LABELS[order.pickup_slot] || order.pickup_slot}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Phone className="w-4 h-4 mt-0.5 text-primary" />
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Customer</div>
                <div className="font-medium">{order.customer_name}</div>
                <div className="text-muted-foreground">{order.customer_phone}</div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" />
            <div>{order.address_line1}{order.address_line2 && `, ${order.address_line2}`}, {order.city} – {order.pincode}</div>
          </div>
        </CardContent>
      </Card>

      {cancelled ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="p-6 flex items-center gap-4">
            <XCircle className="w-8 h-8 text-destructive" />
            <div>
              <div className="font-semibold text-destructive">Order Cancelled</div>
              <div className="text-sm text-muted-foreground mt-1">This pickup was cancelled. Contact us on WhatsApp for a fresh quote.</div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-lg">Status Timeline</CardTitle></CardHeader>
          <CardContent>
            <ol className="relative border-l-2 border-dashed border-border ml-2 space-y-6">
              {STAGES.map((s, i) => {
                const done = i < activeIdx;
                const current = i === activeIdx;
                const pending = i > activeIdx;
                const StageIcon = s.Icon;
                return (
                  <li key={s.key} className="pl-6 relative" data-testid={`timeline-${s.key}`}>
                    <span
                      className={`absolute -left-[13px] top-0 w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                        done ? "bg-emerald-500 text-white border-emerald-500" :
                        current ? "bg-primary text-primary-foreground border-primary tv-pulse" :
                        "bg-background text-muted-foreground border-border"
                      }`}
                    >
                      {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : current ? <StageIcon className="w-3.5 h-3.5" /> : <CircleDashed className="w-3.5 h-3.5" />}
                    </span>
                    <div className={`font-semibold ${pending ? "text-muted-foreground" : ""}`}>{s.label}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{s.desc}</div>
                    {current && (
                      <div className="tv-chip-emerald mt-2 text-[10px]">Current Stage</div>
                    )}
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
